/**
 * Engagement Time Tracker
 *
 * Replicates GA4's internal engagement_time_msec tracking for custom events.
 * Tracks only "active" time — when the tab is visible and the window is focused.
 * Each call to getEngagementDelta() returns the active milliseconds since the last call,
 * mirroring how GA4 attaches engagement time to page_view events.
 */

const isBrowser = typeof window !== 'undefined';

let isActive = true;
let activeStartTime = null;
let accumulatedActiveMs = 0;
let lastReportedMs = 0;
let started = false;

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    pause();
  } else {
    resume();
  }
}

function onFocus() {
  resume();
}

function onBlur() {
  pause();
}

function pause() {
  if (isActive && activeStartTime !== null) {
    accumulatedActiveMs += performance.now() - activeStartTime;
    activeStartTime = null;
  }
  isActive = false;
}

function resume() {
  if (!isActive || activeStartTime === null) {
    isActive = true;
    activeStartTime = performance.now();
  }
}

/**
 * Start tracking engagement time. Call once on app init.
 * Idempotent — calling multiple times is safe.
 */
export function start() {
  if (!isBrowser || started) return;
  started = true;

  isActive = document.visibilityState !== 'hidden' && document.hasFocus();
  activeStartTime = isActive ? performance.now() : null;
  accumulatedActiveMs = 0;
  lastReportedMs = 0;

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);
  window.addEventListener('blur', onBlur);
}

/**
 * Get the total active engagement time in milliseconds since tracking started.
 */
export function getTotalEngagementMs() {
  let total = accumulatedActiveMs;
  if (isActive && activeStartTime !== null) {
    total += performance.now() - activeStartTime;
  }
  return Math.round(total);
}

/**
 * Get the engagement time delta (ms) since the last call to this function.
 * This is the value attached to events as `engagement_time_msec`.
 */
export function getEngagementDelta() {
  const total = getTotalEngagementMs();
  const delta = Math.max(0, total - lastReportedMs);
  lastReportedMs = total;
  return delta;
}

/**
 * Reset the engagement clock. Call on page navigation to get per-page engagement.
 */
export function resetForNavigation() {
  accumulatedActiveMs = 0;
  lastReportedMs = 0;
  activeStartTime = isActive ? performance.now() : null;
}

/**
 * Get current page engagement time (ms since last navigation/reset).
 * Does NOT consume the delta — use for read-only queries.
 */
export function getCurrentPageEngagementMs() {
  return getTotalEngagementMs();
}
